'use client';

import { createContext, type PropsWithChildren, useMemo, useState } from 'react';

import type { Nullable } from '@libs/core';

import type { AlertBanner } from '../types/alertBanner';

export type AlertBannerProps = Record<string, unknown>;

export interface AlertBannerContextProps {
    /** meant to be an alert rendered at the top of the wrapped component tree */
    alert?: AlertBanner | null;
    /** meant to be an alert rendered lower in the wrapped component tree (above a child component) */
    childAlert?: AlertBanner | null;
    alertKey?: number;
    setAlert: (alert: AlertBanner | null | undefined) => void;
    setChildAlert: (alert: AlertBanner | null | undefined) => void;
}
export const AlertBannerContext = createContext<Nullable<AlertBannerContextProps>>(undefined);

export function AlertBannerProvider({ children }: PropsWithChildren<Nullable<AlertBannerProps>>) {
    const [alert, setAlertState] = useState<AlertBanner | null>();
    const [alertKey, setAlertKey] = useState(0);
    const [childAlert, setChildAlert] = useState<AlertBanner | null>();

    const setAlert = useMemo(
        () => (incoming: AlertBanner | null | undefined) => {
            setAlertState(incoming ?? null);
            if (incoming) {
                setAlertKey(k => k + 1);
            }
        },
        []
    );

    const value = useMemo(
        () => ({
            alert,
            alertKey,
            childAlert,
            setAlert,
            setChildAlert,
        }),
        [alert, alertKey, childAlert, setAlert]
    );
    return <AlertBannerContext.Provider value={value}>{children}</AlertBannerContext.Provider>;
}
